import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
	getAllTopSidebarPaths,
	getItemLockStatus,
	getItemPath,
	getParentPathForSubItem,
	topSidebarItems
} from './sidebarItem';
import { userInfoAtom } from '../../../data/atoms/utilsAtoms';
import { useAtomValue } from 'jotai';

import LockIcon from '@mui/icons-material/Lock';
import { useVideoDetailsFromParams } from '../../../Pages/VideoPlayer/hooks/useVideoDetailsFromParams';

const SidebarItems = ({ closeCallback, isSidebarCollapsed }) => {
	const [expandedItems, setExpandedItems] = useState([]);
	const userData = useAtomValue(userInfoAtom);
	const location = useLocation();
	const { typeOfContentView, videoId } = useVideoDetailsFromParams();

	const isCurrentPathActive = (path = '') => {
		return (
			location.pathname.toLowerCase() === path.toLowerCase() ||
			path.toLowerCase() === '/' + typeOfContentView?.toLowerCase()
		);
	};

	const getBackgroundColor = path => {
		return isCurrentPathActive(path) ? { backgroundColor: '#171718', borderRight: '0px solid #3c3c3c' } : {};
	};

	const getColor = path => {
		return isCurrentPathActive(path) ? { color: '#0B6386' } : { color: '#C4C4C4' };
	};

	const isSubMenuExpanded = (subItems = [{ path: '' }]) => {
		return subItems.some(item => expandedItems.includes(item.path.toLowerCase()));
	};

	const toggleExpandableItem = ({ path, subItems = [], shouldClear = false }) => {
		const lowerPath = path.toLowerCase();
		const topLevelItems = getAllTopSidebarPaths();
		const isTopLevelItem = topLevelItems.includes(lowerPath);
		const wasExpanded = expandedItems.includes(lowerPath);
		let newExpandedItems = [];

		if (wasExpanded && shouldClear) {
			// Close the item.
			newExpandedItems = expandedItems.filter(item => item !== lowerPath);
			// If it's a top-level item, also remove its sub-items.
			if (isTopLevelItem) {
				const pathsToRemove = subItems.map(subItem => subItem.path.toLowerCase());
				newExpandedItems = newExpandedItems.filter(item => !pathsToRemove.includes(item));
			}
		} else {
			// Open the item.
			newExpandedItems = [...expandedItems, lowerPath];
		}

		setExpandedItems(() => newExpandedItems);
		return wasExpanded;
	};

	const isItemExpanded = path => {
		return expandedItems.includes(path.toLowerCase());
	};

	useEffect(() => {
		setExpandedItems(() => []);
		const availablePaths = getAllTopSidebarPaths({ includeSubItems: true });
		const topLevelPaths = getAllTopSidebarPaths();
		let hasPathMatchedFromURL = false;
		if (availablePaths.includes(location.pathname.toLowerCase())) {
			hasPathMatchedFromURL = true;
			if (topLevelPaths.includes(location.pathname.toLowerCase())) {
				toggleExpandableItem({ path: location.pathname });
			} else {
				// sub items
				toggleExpandableItem({ path: location.pathname });
				// adding its parent path
				toggleExpandableItem({ path: getParentPathForSubItem(location.pathname) });
			}
		}

		if (availablePaths.some(path => path === typeOfContentView.toLowerCase()) && !hasPathMatchedFromURL) {
			if (topLevelPaths.includes('/' + typeOfContentView.toLowerCase())) {
				toggleExpandableItem({ path: '/' + typeOfContentView });
			} else {
				// sub items
				toggleExpandableItem({ path: '/' + typeOfContentView });
				// adding its parent path
				toggleExpandableItem({ path: getParentPathForSubItem('/' + typeOfContentView) });
			}
		}
	}, [location.pathname, userData.subscriptionId, typeOfContentView, videoId]);

	useEffect(() => {
		if (isSidebarCollapsed) setExpandedItems([]);
	}, [isSidebarCollapsed]);

	return (
		<>
			{topSidebarItems.map(item => {
				// Skip rendering based on various conditions.
				if (item.requiresAuth && !userData.token) return null;
				if (item?.restrictForAdmin && userData.role?.toLowerCase() === 'admin') return null;
				if (item?.restrictForPaidUser && (userData.user?.subscriptionId || userData.subscriptionId || userData.user?.isTrialAvailed)) return null;
				if (
					item.requiredUserTypes?.length &&
					(!userData.user?.userType ||
						!item.requiredUserTypes.some(
							type => type.toLowerCase() === userData.user.userType?.toLowerCase()
						))
				)
					return null;

				const itemPath = getItemPath(item, userData);
				const isItemLocked = getItemLockStatus(item, userData);

				return (
					<li key={item.key} className="nav-item">
						<div className="position-relative">
							<Link
								to={itemPath}
								onClick={closeCallback}
								style={item.noDefaultStyle ? {} : getBackgroundColor(item.path)}
								className="collapse__btn"
							>
								<div className="custom-home-icon">
									<item.icon style={item.noDefaultStyle ? { color: '#C4C4C4' } : getColor(item.path)} />
								</div>
								<span style={item.noDefaultStyle ? { color: '#C4C4C4' } : getColor(item.path)}>{item.label}</span>
							</Link>
							{item.subItems && (
								<button
									type="button"
									onClick={() => toggleExpandableItem({ path: item.path, subItems: item.subItems, shouldClear: true })}
									className="btn border-0 position-absolute top-0 end-0 collapse__btn"
									aria-expanded={isItemExpanded(item.path)}
								>
									<i className={isItemExpanded(item.path) ? 'fa-solid fa-minus' : 'fa-solid fa-plus'}></i>
								</button>
							)}
							{isItemLocked && (
								<button className="btn border-0 position-absolute top-0 end-0">
									<LockIcon />
								</button>
							)}
						</div>
						{item.subItems && (isItemExpanded(item.path) || isSubMenuExpanded(item.subItems)) && (
							<ul className="sub-menu">
								{item.subItems.map(subItem => {
									if (subItem.requiresAuth && !userData.token) return null;
									if (
										subItem.requiredUserTypes?.length &&
										(!userData.user?.userType ||
											!subItem.requiredUserTypes.some(
												type => type.toLowerCase() === userData.user.userType?.toLowerCase()
											))
									)
										return null;

									return (
										<li key={subItem.key}>
											<Link to={subItem.path} onClick={closeCallback} style={getBackgroundColor(subItem.path)}>
												<span style={getColor(subItem.path)}>
													<i className="fa-solid fa-minus" style={getColor(subItem.path)}></i> {subItem.label}
												</span>
											</Link>
										</li>
									);
								})}
							</ul>
						)}
					</li>
				);
			})}
		</>
	);
};

export default SidebarItems;
